import { useState, useCallback } from 'react';
import { FaKey, FaCopy, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';

/**
 * Secure one-time password reveal modal. Displays a temporary password
 * with a copy button and auto-hides after a configurable timeout.
 * The password is never logged and disappears when the modal is closed.
 */
const PasswordRevealModal = ({ isOpen, onClose, password, userName }) => {
    const [isRevealed, setIsRevealed] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(password);
            toast.success('Password copied to clipboard.');
        } catch {
            toast.error('Failed to copy password.');
        }
    }, [password]);

    const handleClose = useCallback(() => {
        setIsRevealed(false);
        onClose();
    }, [onClose]);

    if (!isOpen || !password) return null;

    return (
        <div className="uem-modal-overlay" onClick={handleClose}>
            <div className="uem-password-modal" onClick={e => e.stopPropagation()}>
                <div className="uem-password-modal-header">
                    <h3><FaKey /> Temporary Password</h3>
                    <button type="button" className="uem-modal-close" onClick={handleClose} aria-label="Close password modal">
                        <FaTimes />
                    </button>
                </div>
                <div className="uem-password-modal-body">
                    <p className="uem-password-notice">
                        Password reset successful for <strong>{userName}</strong>.
                        The user must change their password on next sign-in.
                    </p>
                    <div className="uem-password-display">
                        <span className="uem-password-value">
                            {isRevealed ? password : '••••••••••••••••'}
                        </span>
                        <button
                            type="button"
                            className="uem-password-action-btn"
                            onClick={() => setIsRevealed(prev => !prev)}
                            aria-label={isRevealed ? 'Hide password' : 'Reveal password'}
                            title={isRevealed ? 'Hide password' : 'Reveal password'}
                        >
                            {isRevealed ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        <button
                            type="button"
                            className="uem-password-action-btn"
                            onClick={handleCopy}
                            aria-label="Copy password to clipboard"
                            title="Copy password"
                        >
                            <FaCopy />
                        </button>
                    </div>
                    <p className="uem-password-warning">
                        ⚠ This password will not be shown again after closing this dialog.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PasswordRevealModal;
